import { Button } from '@/components/ui';
import { useIframeSso } from '@/hooks/useIframeSso';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

const TWENTY_URL = import.meta.env.VITE_TWENTY_URL || 'https://crm.dev4-kgc.mflerp.com';

export function TwentyCRMPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const {
    iframeRef,
    status: ssoStatus,
    retry: retrySso,
  } = useIframeSso({
    targetOrigin: TWENTY_URL,
    targetSystem: 'twenty',
  });

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const openInNewTab = () => {
    window.open(TWENTY_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4 kgc-card-bg dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500 text-white font-bold">
            CRM
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Twenty CRM</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('integration.crm.subtitle')}
            </p>
          </div>
          {/* SSO Status Indicator */}
          {ssoStatus === 'success' && (
            <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
              {t('integration.sso.active')}
            </span>
          )}
          {(ssoStatus === 'requesting' || ssoStatus === 'sending') && (
            <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
              {t('integration.sso.connecting')}
            </span>
          )}
          {ssoStatus === 'fallback' && (
            <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
              <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
              {t('integration.sso.fallback')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {ssoStatus === 'fallback' && (
            <Button variant="outline" size="sm" onClick={retrySso}>
              {t('integration.sso.retryButton')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setIsLoading(true)}>
            <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t('integration.refresh')}
          </Button>
          <Button size="sm" onClick={openInNewTab}>
            <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            {t('integration.openInNewTab')}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex-1">
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-900 z-10">
            <div className="text-center">
              <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent mx-auto" />
              <p className="text-gray-600 dark:text-gray-400">{t('integration.crm.loading')}</p>
              {ssoStatus === 'fallback' && (
                <p className="mt-2 text-sm text-orange-600 dark:text-orange-400">
                  {t('integration.sso.fallback')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Error State */}
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-900 z-10">
            <div className="text-center max-w-md p-6">
              <div className="mb-4 text-6xl">⚠️</div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('integration.loadFailed')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('integration.crm.errorDescription')}
              </p>
              <div className="space-y-2">
                <Button onClick={openInNewTab} className="w-full">
                  {t('integration.openInNewTab')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setHasError(false);
                    setIsLoading(true);
                  }}
                  className="w-full"
                >
                  {t('integration.retry')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Iframe */}
        <iframe
          ref={iframeRef}
          src={TWENTY_URL}
          title="Twenty CRM"
          className="h-full w-full border-0"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          allow="clipboard-read; clipboard-write"
        />
      </div>
    </div>
  );
}
